import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState<'checking' | 'ready' | 'submitting' | 'success' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);

  // Check for valid recovery session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await authClient.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError('Your password reset link has expired. Please request a new one.');
          setStatus('error');
          return;
        }

        if (!session) {
          // No session - link may have expired or was already used
          setError('Your password reset link has expired or is invalid. Please request a new one.');
          setStatus('error');
          return;
        }

        // Valid session exists - ready to reset password
        setStatus('ready');
      } catch (err) {
        console.error('Error checking session:', err);
        setError('Something went wrong. Please try again.');
        setStatus('error');
      }
    };

    // Small delay to let Supabase process the token from URL hash
    const timer = setTimeout(checkSession, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setStatus('submitting');

    try {
      const { error: updateError } = await authClient.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        setError(updateError.message || 'Failed to update password');
        setStatus('ready');
        return;
      }

      // Success
      setStatus('success');

      // Fire-and-forget: notify worker for Customer.io tracking (Journey 5.4)
      void authClient.auth.getSession().then(({ data: { session: s } }) => {
        if (s?.access_token) {
          apiFetch('/user/password-changed', { method: 'POST', token: s.access_token }).catch(() => {});
        }
      }).catch(() => {});

      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      console.error('Password update error:', err);
      setError('Something went wrong. Please try again.');
      setStatus('ready');
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Loading state - checking session */}
        {status === 'checking' && (
          <div className="text-center p-8">
            <Loader2 size={48} className="animate-spin text-cyan-400 mx-auto mb-4" />
            <p className="text-lg text-slate-300">Verifying your reset link...</p>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div className="text-center p-8">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-lg text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
            >
              Go to Home
            </button>
          </div>
        )}

        {/* Success state */}
        {status === 'success' && (
          <div className="text-center p-8">
            <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
            <p className="text-lg text-green-400">Password updated successfully!</p>
            <p className="text-sm text-slate-400 mt-2">Redirecting to home...</p>
          </div>
        )}

        {/* Ready state - show form */}
        {(status === 'ready' || status === 'submitting') && (
          <div className="bg-[#1A1F26] rounded-xl p-6 shadow-xl">
            <h1 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h1>
            <p className="text-slate-400 text-sm text-center mb-6">
              Enter your new password below
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-4 py-2.5 bg-[#111111] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent pr-10"
                    required
                    minLength={6}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-2.5 bg-[#111111] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-2.5 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
